import { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuCheckboxItem } from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader as AlertHeader,
  AlertDialogFooter, AlertDialogTitle as AlertTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { RichTextEditor } from "@/components/common/RichTextEditor";
import { DatePicker } from "@/components/common/DatePicker";
import { FileUpload } from "@/components/common/FileUpload";
import { StatusBadge, PriorityBadge } from "@/components/common/StatusBadge";
import { TASK_TYPES, TASK_PRIORITIES, TASK_STATUSES } from "@/constants";
import { toast } from "sonner";
import { formatDistanceToNow, differenceInCalendarDays, format } from "date-fns";
import { PushPin, WarningCircle, WarningDiamond, Trash } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

export const TaskModal = ({ task, projectId, onClose, onSaved }) => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [phases, setPhases] = useState([]);
  const [tasksInProject, setTasksInProject] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [commentAttachments, setCommentAttachments] = useState([]);
  const [commentEditorKey, setCommentEditorKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const isTeamMember = user?.role === "team_member";
  const [projectMeta, setProjectMeta] = useState(null);
  const [form, setForm] = useState({
    project_id: task?.project_id || projectId || "",
    phase_id: task?.phase_id || "",
    title: task?.title || "",
    description: task?.description || "",
    assignments: task?.assignments?.length
      ? task.assignments
      : (task?.assigned_to?.length
        ? task.assigned_to.map((uid) => ({ user_id: uid, department: task.department || "" }))
        : [{ user_id: "", department: "" }]),
    task_type: task?.task_type || "Other",
    priority: task?.priority || "Medium",
    start_date: task?.start_date?.slice(0, 10) || "",
    due_date: task?.due_date?.slice(0, 10) || "",
    status: task?.status || "Not Started",
    dependencies: task?.dependencies || [],
    attachments: task?.attachments || [],
    recurring_rule: task?.recurring_rule || "",
    completion_pct: task?.completion_pct ?? 0,
    remark: task?.remark || "",
  });
  const selectedPhase = phases.find((p) => p.id === form.phase_id);
  const statusForPct = (pct, current) => {
    if (pct >= 100) return "Completed";
    if (pct === 0) return current === "Blocked" ? "Blocked" : "Not Started";
    return current === "Blocked" ? "Blocked" : "In Progress";
  };
  const setCompletion = (pct) => setForm((f) => ({ ...f, completion_pct: pct, status: statusForPct(pct, f.status) }));
  const setStatus = (v) => setForm((f) => ({
    ...f, status: v,
    completion_pct: v === "Completed" ? 100 : v === "Not Started" ? 0 : f.completion_pct,
  }));
  const delayDays = task?.status === "Completed" && task?.due_date && task?.actual_completion_date
    ? differenceInCalendarDays(new Date(task.actual_completion_date), new Date(task.due_date)) : 0;

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data));
    if (!projectId) api.get("/projects").then((r) => setProjects(r.data));
  }, [projectId]);

  useEffect(() => {
    if (form.project_id) {
      api.get(`/projects/${form.project_id}/phases`).then((r) => setPhases(r.data));
      api.get(`/projects/${form.project_id}`).then((r) => setProjectMeta(r.data)).catch(() => {});
      api.get("/tasks", { params: { project_id: form.project_id } }).then((r) => setTasksInProject(r.data.filter((t) => t.id !== task?.id)));
    }
  }, [form.project_id, task?.id]);

  const isDraftCreator = !!projectMeta?.is_draft && (projectMeta.created_by || projectMeta.owner_id) === user?.id;
  const canEditDates = isDraftCreator || !task || user?.role === "admin" ||
    (projectMeta && (projectMeta.created_by || projectMeta.owner_id) === user?.id);
  const canEditCompletion = isDraftCreator || !task || (task.assigned_to?.length ? task.assigned_to.includes(user?.id) : !isTeamMember);
  const canEditCore = isDraftCreator || !task || user?.role === "admin" || task.created_by === user?.id ||
    (projectMeta && (projectMeta.created_by || projectMeta.owner_id) === user?.id);
  const lockCore = task ? ((isTeamMember && !isDraftCreator) || !canEditCore) : false;

  useEffect(() => {
    if (task?.id) api.get(`/tasks/${task.id}/comments`).then((r) => setComments(r.data));
  }, [task?.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.start_date || !form.due_date) {
      toast.error("Start date and due date are required");
      return;
    }
    const rows = form.assignments.filter((a) => a.user_id || a.department);
    for (const r of rows) {
      if (!r.user_id) {
        toast.error("Each assignee row needs a person selected");
        return;
      }
    }
    setSaving(true);
    try {
      const payload = { ...form, assignments: rows, phase_id: form.phase_id || null, start_date: form.start_date || null, due_date: form.due_date || null };
      if (task) await api.put(`/tasks/${task.id}`, payload);
      else await api.post("/tasks", payload);
      toast.success(task ? "Task updated" : "Task created");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/tasks/${task.id}`);
      toast.success("Task deleted");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const submitComment = async () => {
    if (!newComment.trim()) return;
    const { data } = await api.post(`/tasks/${task.id}/comments`, { text: newComment, attachments: commentAttachments });
    setComments([...comments, data]);
    setNewComment("");
    setCommentAttachments([]);
    setCommentEditorKey((k) => k + 1);
  };

  const togglePin = async (c) => {
    const { data } = await api.patch(`/comments/${c.id}/pin`);
    setComments(comments.map((cm) => (cm.id === c.id ? data : cm)));
  };

  const updateAssignment = (i, key, val) => setForm((f) => ({
    ...f, assignments: f.assignments.map((a, ai) => {
      if (ai !== i) return a;
      if (key === "user_id") {
        const dept = users.find((u) => u.id === val)?.department || "";
        return { ...a, user_id: val, department: dept || a.department };
      }
      return { ...a, [key]: val };
    }),
  }));
  const addAssignment = () => setForm((f) => (f.assignments.length < 3 ? { ...f, assignments: [...f.assignments, { user_id: "", department: "" }] } : f));
  const removeAssignment = (i) => setForm((f) => ({ ...f, assignments: f.assignments.length > 1 ? f.assignments.filter((_, ai) => ai !== i) : [{ user_id: "", department: "" }] }));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" style={{ borderRadius: "2px" }} data-testid="task-modal">
        <DialogHeader><DialogTitle className="font-display">{task ? "Task Details" : "New Task"}</DialogTitle></DialogHeader>
        <Tabs defaultValue="details">
          <TabsList>
            <TabsTrigger data-testid="task-tab-details" value="details">Details</TabsTrigger>
            {task && <TabsTrigger data-testid="task-tab-comments" value="comments">Comments ({comments.length})</TabsTrigger>}
          </TabsList>
          <TabsContent value="details">
            <form onSubmit={handleSubmit} className="space-y-3 mt-2">
              {task?.blocked_by_incomplete && (
                <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 border border-amber-200 px-3 py-2" style={{ borderRadius: "2px" }} data-testid="task-dependency-warning">
                  <WarningCircle size={14} /> A predecessor task is not yet complete.
                </div>
              )}
              {!projectId && (
                <div>
                  <Label className="text-xs font-semibold">Project</Label>
                  <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })} disabled={!!task}>
                    <SelectTrigger data-testid="task-project-select" style={{ borderRadius: "2px" }}><SelectValue placeholder="Select project" /></SelectTrigger>
                    <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs font-semibold">Title</Label>
                <Input data-testid="task-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required disabled={lockCore} style={{ borderRadius: "2px" }} />
              </div>
              <div>
                <Label className="text-xs font-semibold">Description</Label>
                <div className={lockCore ? "pointer-events-none opacity-60" : ""}>
                  <RichTextEditor testId="task-description-editor" value={form.description} onChange={(v) => setForm({ ...form, description: v })} placeholder="Task details..." />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold">Assigned To <span className="text-muted-foreground font-normal">(department auto-fills from the member, max 3)</span></Label>
                <div className="space-y-2 mt-1">
                  {form.assignments.map((a, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`task-assignment-row-${i}`}>
                      <div className="flex-1">
                        <Select value={a.user_id} onValueChange={(v) => updateAssignment(i, "user_id", v)} disabled={lockCore}>
                          <SelectTrigger data-testid={`task-assignment-user-${i}`} style={{ borderRadius: "2px" }}><SelectValue placeholder="Select person" /></SelectTrigger>
                          <SelectContent>
                            {users.map((u) => (
                              <SelectItem key={u.id} value={u.id} disabled={form.assignments.some((x, xi) => xi !== i && x.user_id === u.id)}>{u.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex-1">
                        <div data-testid={`task-assignment-dept-${i}`}
                          className="h-10 px-3 flex items-center text-sm bg-muted border border-border text-muted-foreground"
                          style={{ borderRadius: "2px" }}>
                          {(users.find((u) => u.id === a.user_id)?.department || a.department) || (a.user_id ? "No department set" : "Department (auto)")}
                        </div>
                      </div>
                      {!lockCore && (
                        <button type="button" data-testid={`task-assignment-remove-${i}`} onClick={() => removeAssignment(i)}
                          className="text-muted-foreground hover:text-destructive text-lg leading-none px-1.5 py-1 shrink-0" title="Remove">×</button>
                      )}
                    </div>
                  ))}
                  {form.assignments.length < 3 && !lockCore && (
                    <button type="button" data-testid="task-assignment-add-btn" onClick={addAssignment}
                      className="text-xs font-semibold uppercase text-primary hover:underline">+ Add assignee ({form.assignments.length}/3)</button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs font-semibold">Type</Label>
                  <Select value={form.task_type} onValueChange={(v) => setForm({ ...form, task_type: v })} disabled={lockCore}>
                    <SelectTrigger data-testid="task-type-select" style={{ borderRadius: "2px" }}><SelectValue /></SelectTrigger>
                    <SelectContent>{TASK_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })} disabled={lockCore}>
                    <SelectTrigger data-testid="task-priority-select" style={{ borderRadius: "2px" }}><SelectValue /></SelectTrigger>
                    <SelectContent>{TASK_PRIORITIES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Status</Label>
                  <Select value={form.status} onValueChange={setStatus} disabled={!canEditCompletion}>
                    <SelectTrigger data-testid="task-status-select" style={{ borderRadius: "2px" }}><SelectValue /></SelectTrigger>
                    <SelectContent>{TASK_STATUSES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              {task && !canEditCore && !isTeamMember && (
                <p className="text-[11px] text-muted-foreground font-mono" data-testid="task-core-locked-hint">&gt; details locked — only the task creator or project creator can edit these fields</p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold">Start Date <span className="text-destructive">*</span></Label>
                  <DatePicker testId="task-start-date-input" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} disabled={task ? (isTeamMember || !canEditDates) : false} />
                </div>
                <div>
                  <Label className="text-xs font-semibold">Due Date <span className="text-destructive">*</span></Label>
                  <DatePicker testId="task-due-date-input" value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} disabled={task ? (isTeamMember || !canEditDates) : false} />
                </div>
              </div>
              {task && !canEditDates && !isTeamMember && (
                <p className="text-[11px] text-muted-foreground font-mono" data-testid="task-dates-locked-hint">&gt; timeline locked — only the project creator can change start/due dates</p>
              )}
              {delayDays > 0 && (
                <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2" style={{ borderRadius: "2px" }} data-testid="task-delay-badge">
                  <WarningDiamond size={14} weight="fill" />
                  Completed {format(new Date(task.actual_completion_date), "d MMM yyyy")} — {delayDays} day{delayDays > 1 ? "s" : ""} after due date
                </div>
              )}
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold">Completion</Label>
                  <span className="text-xs font-mono font-semibold text-primary" data-testid="task-completion-value">{form.completion_pct}%</span>
                </div>
                <div className="flex items-center gap-3 mt-1">
                  <input type="range" min={0} max={100} step={5} value={form.completion_pct}
                    data-testid="task-completion-slider" disabled={!canEditCompletion}
                    onChange={(e) => setCompletion(Number(e.target.value))}
                    className="flex-1 accent-primary disabled:opacity-50 disabled:cursor-not-allowed" />
                  <Input type="number" min={0} max={100} value={form.completion_pct}
                    data-testid="task-completion-input" disabled={!canEditCompletion}
                    onChange={(e) => setCompletion(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                    className="w-16 text-center" style={{ borderRadius: "2px" }} />
                </div>
                {task && !canEditCompletion && (
                  <p className="text-[11px] text-muted-foreground font-mono mt-1" data-testid="task-completion-locked-hint">&gt; status & completion locked — only the assigned person can update them (status auto-syncs with progress)</p>
                )}
              </div>
              <div>
                <Label className="text-xs font-semibold">Remark</Label>
                <Textarea data-testid="task-remark-input" value={form.remark} rows={2}
                  onChange={(e) => setForm({ ...form, remark: e.target.value })}
                  placeholder="Add a remark / progress note..." style={{ borderRadius: "2px" }} />
              </div>
              {!isTeamMember && (
                <div>
                  <Label className="text-xs font-semibold">Phase</Label>
                  <Select value={form.phase_id} onValueChange={(v) => setForm({ ...form, phase_id: v })} disabled={lockCore}>
                    <SelectTrigger data-testid="task-phase-select" style={{ borderRadius: "2px" }}><SelectValue placeholder="No phase" /></SelectTrigger>
                    <SelectContent>{phases.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                  {selectedPhase && (
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1.5" data-testid="task-phase-dates-readonly">
                      <span>Phase Start: <span className="font-mono">{selectedPhase.start_date ? format(new Date(selectedPhase.start_date), "d MMM yyyy") : "—"}</span></span>
                      <span>Phase End: <span className="font-mono">{selectedPhase.target_end_date ? format(new Date(selectedPhase.target_end_date), "d MMM yyyy") : "—"}</span></span>
                    </div>
                  )}
                </div>
              )}
              {!isTeamMember && tasksInProject.length > 0 && (
                <div>
                  <Label className="text-xs font-semibold">Depends On (blocks this task)</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {tasksInProject.map((t) => (
                      <label key={t.id} className="flex items-center gap-1.5 text-xs border border-border px-2 py-1 cursor-pointer" style={{ borderRadius: "2px" }}>
                        <input type="checkbox" data-testid={`task-dependency-checkbox-${t.id}`} checked={form.dependencies.includes(t.id)} disabled={lockCore}
                          onChange={(e) => setForm({ ...form, dependencies: e.target.checked ? [...form.dependencies, t.id] : form.dependencies.filter((d) => d !== t.id) })} />
                        {t.title}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {!isTeamMember && (
                <div>
                  <Label className="text-xs font-semibold">Recurring</Label>
                  <Select value={form.recurring_rule || "none"} onValueChange={(v) => setForm({ ...form, recurring_rule: v === "none" ? "" : v })} disabled={lockCore}>
                    <SelectTrigger data-testid="task-recurring-select" style={{ borderRadius: "2px" }}><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="none">None</SelectItem><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="monthly">Monthly</SelectItem></SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs font-semibold">Attachments</Label>
                <FileUpload attachments={form.attachments} onChange={(a) => setForm({ ...form, attachments: a })} />
              </div>
              <div className="flex items-center justify-between pt-2">
                {task && !isTeamMember ? (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button type="button" data-testid="task-modal-delete-btn" className="text-destructive text-xs font-semibold flex items-center gap-1 hover:underline">
                        <Trash size={14} />Delete Task
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertHeader>
                        <AlertTitle>Delete this task?</AlertTitle>
                        <AlertDialogDescription>This will permanently remove "{task.title}". This cannot be undone.</AlertDialogDescription>
                      </AlertHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction data-testid="task-modal-confirm-delete-btn" onClick={handleDelete}>Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                ) : <span />}
                <div className="flex gap-2">
                  <button type="button" data-testid="task-modal-cancel-btn" onClick={onClose} className="btn-outline-navy">Cancel</button>
                  <button type="submit" data-testid="task-modal-save-btn" disabled={saving || !form.project_id} className="btn-navy">{saving ? "Saving..." : "Save Task"}</button>
                </div>
              </div>
            </form>
          </TabsContent>
          {task && (
            <TabsContent value="comments">
              <div className="space-y-3 mt-2 max-h-72 overflow-y-auto">
                {comments.map((c) => (
                  <div key={c.id} data-testid={`comment-item-${c.id}`} className={`p-3 border border-border ${c.pinned ? "bg-amber-50" : ""}`} style={{ borderRadius: "2px" }}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold">{c.user_name}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">{formatDistanceToNow(new Date(c.created_at), { addSuffix: true })}</span>
                        <button data-testid={`comment-pin-btn-${c.id}`} onClick={() => togglePin(c)}><PushPin size={13} weight={c.pinned ? "fill" : "regular"} /></button>
                      </div>
                    </div>
                    <div className="text-sm mt-1" dangerouslySetInnerHTML={{ __html: c.text }} />
                  </div>
                ))}
                {comments.length === 0 && <div className="text-sm font-mono text-muted-foreground">&gt; no comments yet</div>}
              </div>
              <div className="mt-3 space-y-2">
                <RichTextEditor key={commentEditorKey} testId="new-comment-editor" value={newComment} onChange={setNewComment} placeholder="Add a comment... use @Name to mention" />
                <FileUpload attachments={commentAttachments} onChange={setCommentAttachments} />
                <button data-testid="submit-comment-btn" onClick={submitComment} className="btn-navy">Post Comment</button>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
